import { isDefined } from '@aloft/toolkit-shared';

import type mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';

import { getPropertyPinIcon } from '../Tkv2Map/utils/getPropertyPinIcon';
import type MapBox from './MapBox';
import { MAP_LAYER_DEFINITIONS, MAP_SOURCES } from './constants';
import type { Coordinates } from './types';

type UseUpdateSubjectPinProps = {
  mapRef: React.MutableRefObject<MapBox | null>;
  coordinates: Coordinates | undefined;
};

export const useUpdateSubjectPin = ({ mapRef, coordinates }: UseUpdateSubjectPinProps) => {
  useEffect(() => {
    const map = mapRef.current?.getMap();

    if (!map || !isDefined(coordinates)) return;

    const updatePin = () => {
      // Get or create the PINS source
      let source = map.getSource(MAP_SOURCES.PINS) as mapboxgl.GeoJSONSource;

      if (!source) {
        // Create the PINS source if it doesn't exist
        map.addSource(MAP_SOURCES.PINS, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
        source = map.getSource(MAP_SOURCES.PINS) as mapboxgl.GeoJSONSource;

        // Add the PINS layer if it doesn't exist
        if (!map.getLayer(MAP_LAYER_DEFINITIONS.PINS.id)) {
          map.addLayer(MAP_LAYER_DEFINITIONS.PINS);
        }
      }

      // Get the Subject icon name
      const icon = getPropertyPinIcon('Subject', false);

      // Update the source with the subject pin
      source.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [coordinates.lng, coordinates.lat], // [lng, lat] Mapbox format
            },
            properties: {
              icon,
              id: 'subject',
              idx: 0,
            },
          },
        ],
      });
    };

    // If map is loaded, update immediately
    if (map.isStyleLoaded()) {
      updatePin();
    } else {
      // Otherwise, wait for the map to load
      const handleLoad = () => {
        updatePin();
      };
      map.once('load', handleLoad);
      return () => {
        map.off('load', handleLoad);
      };
    }
  }, [mapRef, coordinates]);
};
